import { Fragment, useState } from "react";
import { LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { num, rupee, pnlClass, istDateTime, spreadText, dirClass } from "@/lib/api";
import { PayoffChart } from "@/components/PayoffChart";

const OPEN = ["open", "entering", "exiting"];

export const TradesTable = ({ title, rows, id, onExit, busy }) => {
  const [exp, setExp] = useState({});
  return (
  <div className="panel col-span-12" data-testid={`${id}-panel`}>
    <div className="border-b border-slate-800 px-4 py-2.5"><span className="eyebrow">{title}</span></div>
    <div className="overflow-x-auto">
      <table className="dense num w-full" data-testid={id}>
        <thead><tr><th>Opened</th><th>Spread</th><th>Qty</th><th>Net entry</th><th>Current/Exit</th><th>Stop</th><th>Target</th><th>Status</th><th>P&amp;L</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={10} className="!py-5 !text-center text-slate-500">None.</td></tr>}
          {rows.map((t) => (
            <Fragment key={t.id}>
            <tr>
              <td>{istDateTime(t.opened_at)}</td>
              <td className={dirClass(t.direction)}>{t.legs && <span className="mr-1 text-slate-400">{t.strategy}</span>}{spreadText(t)}</td>
              <td>{t.qty}</td><td>{num(t.debit)}</td><td>{num(t.exit_value ?? t.current_value)}</td>
              <td className="down">{num(t.stop_value)}</td><td className="up">{num(t.target_value)}</td>
              <td className="uppercase text-slate-400">{t.status}{t.exit_reason ? ` · ${t.exit_reason}` : ""}{t.abort_reason ? ` · ${t.abort_reason}` : ""}</td>
              <td className={pnlClass(t.pnl ?? t.unrealized)}>{rupee(t.pnl ?? t.unrealized)}</td>
              <td className="whitespace-nowrap">
                {OPEN.includes(t.status) && (
                  <button data-testid={`button-payoff-${t.id}`} title="Payoff at expiry" onClick={() => setExp((e) => ({ ...e, [t.id]: !e[t.id] }))}
                    className={`mr-1 inline-flex h-6 items-center gap-1 rounded border px-1.5 text-[11px] transition-colors ${exp[t.id] ? "border-indigo-500/60 text-indigo-200" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                    <LineChart size={11} /> Payoff
                  </button>
                )}
                {onExit && t.status === "open" && <Button data-testid={`button-live-exit-${t.id}`} size="sm" variant="outline" disabled={busy} onClick={() => onExit(t.id)} className="h-6 border-slate-700 px-2 text-[11px]">Exit</Button>}
              </td>
            </tr>
            {exp[t.id] && OPEN.includes(t.status) && (
              <tr data-testid={`payoff-row-${t.id}`}><td colSpan={10} className="!px-4 !py-3 !text-left"><div className="sticky left-4" style={{ width: "min(960px, calc(100vw - 64px))" }}><PayoffChart trade={t} id={`payoff-${t.id}`} /></div></td></tr>
            )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  </div>
  );
};


export const AuditTable = ({ rows, id, title, empty = "No live events yet." }) => (
      <div className="panel col-span-12" data-testid={id}>
    <div className="border-b border-slate-800 px-4 py-2.5"><span className="eyebrow">{title}</span></div>
    <div className="max-h-[360px] overflow-auto">
      <table className="dense num w-full">
        <thead className="sticky top-0 bg-[#0D121F]"><tr><th>Time</th><th className="!text-left">Event</th><th className="!text-left">Details</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={3} className="!py-6 !text-center text-slate-500">{empty}</td></tr>}
          {rows.map((a) => {
            const { id, ts, event, level, date, dry_run: _dry, ...rest } = a;
            return (
              <tr key={id}>
                <td className="text-slate-500">{istDateTime(ts)}</td>
                <td className={`!text-left ${level === "error" ? "down" : level === "warn" ? "text-amber-400" : "text-slate-300"}`}>{event}</td>
                <td className="max-w-[600px] truncate !text-left text-slate-500" title={JSON.stringify(rest)}>{Object.keys(rest).length ? JSON.stringify(rest) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
